import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import SearchIcon from "@mui/icons-material/Search";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatusChip, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
      <Box sx={{ color: "text.secondary", mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500, wordBreak: "break-word" }}>{value || "—"}</Typography>
      </Box>
    </Stack>
  );
}

export default function InventoryDetailNew() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [searchType, setSearchType] = useState("itemID");
  const [searchTerm, setSearchTerm] = useState(sp.get("id") || "");
  const [item, setItem] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async (term = searchTerm, type = searchType) => {
    if (!term) return;
    setLoading(true);
    setItem(null);
    setList([]);
    try {
      const url = type === "itemID"
        ? `/api/inventory/getinventorydetail/${term}`
        : `/api/inventory/box/${term}`;
      const res = await api.get(url);
      if (type === "itemID") setItem(res.data);
      else setList(Array.isArray(res.data) ? res.data : [res.data]);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (sp.get("id")) doSearch(sp.get("id"), "itemID");
  }, []);

  return (
    <Box p={3}>
      <PageHeader
        title="Inventory Detail"
        subtitle="Lookup by item ID or storage box"
        icon={<QrCodeScannerOutlinedIcon />}
        actions={[<Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => nav("/InventoryList")}>Back to Inventory</Button>]}
      />

      <SectionCard sx={{ mb: 3 }} accent="#6C50BF">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            select size="small" value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="itemID">By Item ID</MenuItem>
            <MenuItem value="boxID">By Box ID</MenuItem>
          </TextField>
          <TextField
            size="small" fullWidth placeholder="Enter ID…"
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={() => doSearch()} disabled={loading}>
            {loading ? <CircularProgress size={16} color="inherit" /> : "Lookup"}
          </Button>
        </Stack>
      </SectionCard>

      {loading && !item && list.length === 0 && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}

      {item && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3, mb: 1, borderRadius: 4,
                background: "linear-gradient(135deg, #6C50BF 0%, #8568D4 100%)",
                color: "#fff", position: "relative", overflow: "hidden",
              }}
            >
              <Box sx={{ position: "absolute", right: -60, top: -60, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
              <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }} sx={{ position: "relative" }}>
                <Box sx={{ width: 80, height: 80, borderRadius: 3, bgcolor: "#fff", color: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Inventory2OutlinedIcon sx={{ fontSize: 40 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 0.5 }}>{item.itemName}</Typography>
                  <Typography sx={{ fontSize: 13, opacity: 0.9, mb: 1 }}>{item.brandModel}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={item.itemID} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, fontFamily: "monospace" }} />
                    <Chip label={item.category} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600 }} />
                    <StatusChip status={item.status || "In Stock"} />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Item Information" accent="#6C50BF">
              <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Serial Number" value={item.serialNumber} />
              <InfoRow icon={<CategoryOutlinedIcon fontSize="small" />} label="Category" value={item.category} />
              <InfoRow icon={<Inventory2OutlinedIcon fontSize="small" />} label="Brand / Model" value={item.brandModel} />
              <InfoRow icon={<PersonOutlineOutlinedIcon fontSize="small" />} label="Supplier" value={item.supplier} />
              <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Storage Box" value={item.boxID} />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Purchase & Warranty" accent="#F9BC06">
              <InfoRow icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Purchase Date" value={item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString("en-IN") : null} />
              <InfoRow icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Warranty Expiry" value={item.warrantyExpiryDate ? new Date(item.warrantyExpiryDate).toLocaleDateString("en-IN") : null} />
              <InfoRow icon={<CurrencyRupeeOutlinedIcon fontSize="small" />} label="Price" value={item.price ? `₹${Number(item.price).toLocaleString("en-IN")}` : null} />
              <InfoRow icon={<Inventory2OutlinedIcon fontSize="small" />} label="Stock" value={item.stock} />
            </SectionCard>
          </Grid>

          {item.assignedTo && (
            <Grid item xs={12}>
              <SectionCard title="Assignment" subtitle="Who is using this asset?" accent="#39C645">
                <Grid container>
                  <Grid item xs={12} sm={4}><InfoRow icon={<PersonOutlineOutlinedIcon fontSize="small" />} label="Assigned To" value={item.assignedTo} /></Grid>
                  <Grid item xs={12} sm={4}><InfoRow icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Assigned Date" value={item.assignmentDate ? new Date(item.assignmentDate).toLocaleDateString("en-IN") : null} /></Grid>
                  <Grid item xs={12} sm={4}><InfoRow icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Return Date" value={item.returnDate ? new Date(item.returnDate).toLocaleDateString("en-IN") : "Open-ended"} /></Grid>
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {item.notes && (
            <Grid item xs={12}>
              <SectionCard title="Notes" accent="#5C85FF">
                <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{item.notes}</Typography>
              </SectionCard>
            </Grid>
          )}
        </Grid>
      )}

      {list.length > 0 && (
        <SectionCard title="Items in Box" subtitle={`${list.length} items`}>
          <Grid container spacing={2}>
            {list.map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i.itemID}>
                <Box
                  sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}
                  onClick={() => { setSearchType("itemID"); setSearchTerm(i.itemID); doSearch(i.itemID, "itemID"); }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Inventory2OutlinedIcon color="primary" />
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{i.itemName}</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: "text.secondary", mb: 1 }}>{i.itemID}</Typography>
                  <StatusChip status={i.status || "In Stock"} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      )}

      {!loading && !item && list.length === 0 && searchTerm && (
        <SectionCard>
          <EmptyState
            icon={<WarningAmberOutlinedIcon />}
            title="No results"
            subtitle={`Nothing found for ${searchType === "itemID" ? "item" : "box"} "${searchTerm}"`}
          />
        </SectionCard>
      )}
    </Box>
  );
}
